/**
 * Conservation des jetons de session du backoffice.
 *
 * Le backend emet des **jetons opaques**, jamais des JWT : rien n'est lisible
 * cote client, ni date d'expiration ni identite. Les deux dates fournies par
 * `/auth/login` sont donc conservees telles quelles pour piloter le
 * rafraichissement proactif.
 *
 * Stockage : `sessionStorage`, pas `localStorage`. Le compromis est assume et il
 * penche du cote de la duree de vie la plus courte possible pour un outil
 * d'administration : la session meurt avec l'onglet, ce qui limite la fenetre
 * d'exploitation d'un vol de jeton sur un poste partage. Un `httpOnly` cookie
 * serait meilleur, mais le backend n'en emet aucun — il attend un en-tete
 * `Authorization` (voir `CurrentIdentityUserProvider`), et le pretendre
 * autrement serait faux.
 *
 * Aucune valeur n'est jamais journalisee : un jeton dans la console est un
 * secret dans les logs (AGENTS.md, section Securite).
 */

export interface SessionTokens {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly refreshToken: string;
  readonly refreshTokenExpiresAt: string;
}

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly emailVerified: boolean;
  /**
   * Nom d'affichage du titulaire, ou `null` s'il ne s'est pas nomme.
   *
   * Facultatif dans ce type, et pas seulement parce que le serveur peut rendre
   * `null` : une session **deja ecrite** dans `sessionStorage` avant cette
   * version n'en porte pas du tout. La marquer obligatoire ferait echouer
   * `isSessionUser` sur cette session, donc deconnecterait l'utilisateur au
   * rechargement suivant la mise en production.
   */
  readonly displayName?: string | null;
}

export interface StoredSession {
  readonly tokens: SessionTokens;
  readonly user: SessionUser;
}

const STORAGE_KEY = 'miamiam.backoffice.session';

/**
 * Marge avant expiration au-dela de laquelle le jeton est renouvele.
 *
 * Une minute : assez pour couvrir une requete lente et le decalage d'horloge
 * entre le poste et le serveur, assez peu pour ne pas renouveler a chaque appel.
 */
const REFRESH_MARGIN_MS = 60_000;

const isSessionTokens = (value: unknown): value is SessionTokens => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    typeof record.accessToken === 'string' &&
    typeof record.accessTokenExpiresAt === 'string' &&
    typeof record.refreshToken === 'string' &&
    typeof record.refreshTokenExpiresAt === 'string'
  );
};

const isSessionUser = (value: unknown): value is SessionUser => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    typeof record.id === 'string' &&
    typeof record.email === 'string' &&
    typeof record.emailVerified === 'boolean'
  );
};

export const isStoredSession = (value: unknown): value is StoredSession => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return isSessionTokens(record.tokens) && isSessionUser(record.user);
};

export interface TokenStorage {
  read(): StoredSession | null;
  write(session: StoredSession): void;
  clear(): void;
}

/**
 * Implementation sur `Storage`.
 *
 * @param storage injecte pour que les tests n'aient pas besoin d'un navigateur
 */
export const createTokenStorage = (storage: Storage): TokenStorage => ({
  read: (): StoredSession | null => {
    let raw: string | null;

    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      // `sessionStorage` leve quand le navigateur refuse le stockage (mode
      // privé restrictif, cookies tiers bloques dans une iframe). Traiter le
      // cas comme « pas de session » vaut mieux qu'un ecran blanc.
      return null;
    }

    if (raw === null) {
      return null;
    }

    let parsed: unknown;

    try {
      parsed = JSON.parse(raw);
    } catch {
      // Contenu corrompu : on le jette plutot que de boucler sur une erreur de
      // lecture a chaque appel.
      return null;
    }

    return isStoredSession(parsed) ? parsed : null;
  },

  write: (session: StoredSession): void => {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Sans stockage, la session ne survit pas au rechargement. L'application
      // reste utilisable le temps de l'onglet grace au cache memoire de
      // `httpClient`, ce qui est preferable a un echec de connexion.
    }
  },

  clear: (): void => {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // Rien a faire : l'appelant considere de toute facon la session comme
      // terminee.
    }
  },
});

/**
 * Le jeton d'acces doit-il etre renouvele avant d'etre utilise ?
 *
 * Une date illisible est traitee comme **expiree** : mieux vaut un
 * rafraichissement inutile qu'un 401 en pleine navigation.
 */
export const accessTokenNeedsRefresh = (tokens: SessionTokens, now: Date): boolean => {
  const expiresAt = Date.parse(tokens.accessTokenExpiresAt);

  if (Number.isNaN(expiresAt)) {
    return true;
  }

  return expiresAt - now.getTime() <= REFRESH_MARGIN_MS;
};

/**
 * Le jeton de rafraichissement est-il encore utilisable ?
 *
 * Repondre non evite un appel voue a l'echec, donc une deconnexion plus lente et
 * une erreur reseau affichee au passage.
 */
export const refreshTokenIsUsable = (tokens: SessionTokens, now: Date): boolean => {
  const expiresAt = Date.parse(tokens.refreshTokenExpiresAt);

  if (Number.isNaN(expiresAt)) {
    return false;
  }

  return expiresAt > now.getTime();
};
